import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ListItemNode } from '@lexical/list';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { $dfs, $getNearestNodeOfType } from '@lexical/utils';
import { RichText } from '@lexkit/editor';
import { Box, SxProps, Theme } from '@mui/material';
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  KEY_TAB_COMMAND,
  type ElementNode,
  type LexicalNode,
  type PointType,
  type RangeSelection,
} from 'lexical';

import type { DictationControls } from './DictationToolbarButton';
import {
  RichTextProvider,
  richTextEditorTheme,
  richTextExtensions,
  useRichTextEditor,
} from './editor';
import { LinkDialog } from './LinkDialog';
import { LinkDialogProvider } from './LinkDialogContext';
import { LinkPopover } from './LinkPopover';
import { RichTextEditorToolbar } from './RichTextEditorToolbar';

type SerializedEditorState = Record<string, unknown>;

export interface RichTextEditorHandle {
  insertImage: (src: string, alt: string) => void;
  /**
   * Inserts `text` at the caret, spaced off any adjacent words. Falls back to
   * appending at the end when the editor has no caret.
   */
  insertText: (text: string) => void;
}

type RichTextEditorProps = Readonly<{
  id?: string;
  /** Lexical `EditorState.toJSON()` output to preload the editor. */
  initialValue?: SerializedEditorState | null;
  /** Called with the latest JSON state any time the content changes. */
  onChange?: (state: SerializedEditorState) => void;
  /** When true, renders read-only content without a toolbar. */
  readOnly?: boolean;
  placeholder?: string;
  /** Hide the toolbar even when editable (rarely needed). */
  hideToolbar?: boolean;
  /** When provided, shows image button in toolbar; opens caller's file picker. */
  onInsertImage?: () => void;
  /** When provided, shows paperclip button in toolbar; opens caller's file picker. */
  onAttachFile?: () => void;
  /** Combined inline images + chips count remaining. Disables image/attach buttons at 0. */
  attachmentSlotsRemaining?: number;
  /** When provided, shows a mic button in the toolbar for dictating into the editor. */
  dictation?: DictationControls;
  /** Required for `dictation` — the transcript reaches the editor only through this. */
  controlRef?: MutableRefObject<RichTextEditorHandle | null>;
  /** When true and editable, links can be opened from a popover at the caret or with Ctrl/Cmd + click. */
  openableLinks?: boolean;
}>;

/**
 * Rich text editor for coach notes. Wraps LexKit with our extension set and
 * design-token-aware theme.
 *
 * Use `readOnly` for accordion display surfaces; omit for edit mode.
 */
export const RichTextEditor = ({
  id,
  initialValue,
  onChange,
  readOnly = false,
  placeholder = 'Start typing…',
  hideToolbar = false,
  onInsertImage,
  onAttachFile,
  attachmentSlotsRemaining,
  dictation,
  controlRef,
  openableLinks = false,
}: RichTextEditorProps) => {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const handleOpenLinkDialog = useCallback(() => setLinkDialogOpen(true), []);
  const handleCloseLinkDialog = useCallback(() => setLinkDialogOpen(false), []);

  const linkDialogCtx = useMemo(
    () => ({ openLinkDialog: handleOpenLinkDialog }),
    [handleOpenLinkDialog],
  );

  return (
    <LinkDialogProvider value={linkDialogCtx}>
      <RichTextProvider extensions={richTextExtensions} config={{ theme: richTextEditorTheme }}>
        <Box
          id={id}
          sx={readOnly ? richTextReadOnlySx : richTextEditableSx}
          data-testid="rich-text-editor"
        >
          {!readOnly && !hideToolbar && (
            <RichTextEditorToolbar
              onOpenLinkDialog={handleOpenLinkDialog}
              onInsertImage={onInsertImage}
              onAttachFile={onAttachFile}
              attachmentSlotsRemaining={attachmentSlotsRemaining}
              dictation={dictation}
            />
          )}
          <RichText
            placeholder={readOnly ? '' : placeholder}
            classNames={{
              contentEditable: 'rt-content',
              placeholder: 'rt-placeholder',
            }}
          />
          <EditorLifecycle initialValue={initialValue} onChange={onChange} readOnly={readOnly} />
          {!readOnly && controlRef && <EditorController controlRef={controlRef} />}
          {!readOnly && <ListTabHandler />}
          {readOnly && <ClickableLinkPlugin newTab />}
          {!readOnly && <LinkDialog open={linkDialogOpen} onClose={handleCloseLinkDialog} />}
          {!readOnly && openableLinks && <LinkPopover />}
        </Box>
      </RichTextProvider>
    </LinkDialogProvider>
  );
};

/**
 * Imperatively drives the raw Lexical editor for concerns LexKit doesn't expose
 * as props: initial state hydration, editable toggle, and change notifications.
 *
 * Must render inside `RichTextProvider`.
 */
const EditorLifecycle = ({
  initialValue,
  onChange,
  readOnly,
}: Pick<RichTextEditorProps, 'initialValue' | 'onChange' | 'readOnly'>) => {
  const { editor, listeners, import: editorImport, export: editorExport } = useRichTextEditor();
  const hasHydrated = useRef(false);

  // One-time initial state hydration. Always reset — even when initialValue is
  // null — to clear any content the underlying editor instance may retain when
  // this component is remounted or reused.
  useEffect(() => {
    if (!editor || hasHydrated.current) return;

    if (initialValue) {
      editorImport.fromJSON(initialValue);
    } else {
      editor.update(() => {
        const root = $getRoot();

        root.clear();
        root.append($createParagraphNode());
      });
    }

    hasHydrated.current = true;
  }, [editor, editorImport, initialValue]);

  // Keep editable state in sync with prop
  useEffect(() => {
    if (!editor) return;

    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  // Fire onChange with latest JSON state
  useEffect(() => {
    if (!editor || !onChange) return;

    const unregister = listeners.registerUpdate?.(() => {
      onChange(editorExport.toJSON());
    });

    return () => {
      unregister?.();
    };
  }, [editor, listeners, editorExport, onChange]);

  return null;
};

/**
 * Nearest non-empty text either side of `node` within its block. Formatting runs
 * and links split a paragraph into separate nodes — and nest it — so the
 * neighbouring character usually lives outside the node holding the caret. Stops
 * at the block, where a paragraph break already separates the words.
 */
const textBeside = (node: LexicalNode, direction: 'before' | 'after'): string => {
  const step = (from: LexicalNode) =>
    direction === 'before' ? from.getPreviousSibling() : from.getNextSibling();

  let current: LexicalNode | null = node;

  while (current) {
    for (let sibling = step(current); sibling; sibling = step(sibling)) {
      const text = sibling.getTextContent();

      if (text) return text;
    }

    const parent: ElementNode | null = current.getParent();

    current = parent?.isInline() ? parent : null;
  }

  return '';
};

/** Character immediately before `point`, crossing node and inline boundaries. */
const charBefore = (point: PointType): string => {
  const node = point.getNode();

  if (!$isTextNode(node)) return '';

  if (point.offset > 0) return node.getTextContent().charAt(point.offset - 1);

  return textBeside(node, 'before').slice(-1);
};

const charAfter = (point: PointType): string => {
  const node = point.getNode();

  if (!$isTextNode(node)) return '';

  const content = node.getTextContent();

  if (point.offset < content.length) return content.charAt(point.offset);

  return textBeside(node, 'after').charAt(0);
};

/**
 * Pads `text` with the spaces needed to keep it from running into the words on
 * either side. Measured from the edges of `range`, so replacing a selection is
 * padded against what survives it rather than what it removed.
 */
const spacedForRange = (range: RangeSelection, text: string): string => {
  const [start, end] = range.isBackward()
    ? [range.focus, range.anchor]
    : [range.anchor, range.focus];
  const lead = /\S/.test(charBefore(start)) ? ' ' : '';
  const trail = /\S/.test(charAfter(end)) ? ' ' : '';

  return `${lead}${text}${trail}`;
};

/** Exposes editor command surface (image and text insertion) to the parent via ref. */
const EditorController = ({
  controlRef,
}: {
  controlRef: MutableRefObject<RichTextEditorHandle | null>;
}) => {
  const { editor, commands } = useRichTextEditor();

  useImperativeHandle(
    controlRef,
    () => ({
      insertImage: (src, alt) => {
        if (!editor) return;

        const imageKeysBefore = new Set<string>();

        editor.getEditorState().read(() => {
          for (const { node } of $dfs()) {
            if (node.getType() === 'image') imageKeysBefore.add(node.getKey());
          }
        });

        commands.insertImage({ src, alt });

        // The image is a block DecoratorNode; without an editable node after it
        // the caret has nowhere to render. Drop a paragraph after it and select it.
        editor.update(() => {
          const inserted = $dfs().find(
            ({ node }) => node.getType() === 'image' && !imageKeysBefore.has(node.getKey()),
          )?.node;

          if (!inserted) return;

          const next = inserted.getNextSibling();

          if ($isParagraphNode(next) && next.isEmpty()) {
            next.selectStart();

            return;
          }

          const paragraph = $createParagraphNode();

          inserted.insertAfter(paragraph);
          paragraph.selectStart();
        });
      },
      insertText: (text) => {
        if (!editor) return;

        editor.update(() => {
          const selection = $getSelection();
          // An async insert can arrive after the editor has lost its caret;
          // append rather than drop the text.
          const range = $isRangeSelection(selection) ? selection : $getRoot().selectEnd();

          range.insertText(spacedForRange(range, text));
        });
      },
    }),
    [editor, commands],
  );

  return null;
};

/**
 * Tab indents the current list item; Shift+Tab outdents. Tab outside a list
 * falls through so it can still move focus out of the editor.
 */
const ListTabHandler = () => {
  const { editor, commands } = useRichTextEditor();

  useEffect(() => {
    if (!editor) return;

    return editor.registerCommand<KeyboardEvent>(
      KEY_TAB_COMMAND,
      (event) => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) return false;

        const anchorNode = selection.anchor.getNode();
        const listItem = $getNearestNodeOfType(anchorNode, ListItemNode);

        if (!listItem) return false;

        event.preventDefault();
        if (event.shiftKey) {
          commands.outdentList();
        } else {
          commands.indentList();
        }

        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, commands]);

  return null;
};

const baseContentSx = ({ backrs, typography }: Theme): Record<string, unknown> => ({
  '.rt-content': {
    ...typography['text-md-regular'],
    minHeight: '150px',
    padding: backrs.spacing['spacing-none'],
  },
  '.rt-placeholder': {
    ...typography['text-md-regular'],
    color: backrs.colors['Gray/500'],
    position: 'absolute',
    top: 0,
    left: 0,
    padding: backrs.spacing['spacing-3xl'],
    pointerEvents: 'none',
    userSelect: 'none',
  },
  '.rt-paragraph': {
    margin: 0,
  },
  '.rt-list-ul, .rt-list-ol': {
    margin: 0,
    paddingLeft: backrs.spacing['spacing-2xl'],
    marginLeft: backrs.spacing['spacing-md'],
    marginBottom: backrs.spacing['spacing-sm'],
  },
  '.rt-list-item': {
    marginBottom: backrs.spacing['spacing-xs'],
  },
  '.rt-list-item-nested': {
    listStyle: 'none',
  },
  '.rt-list-item-nested::before, .rt-list-item-nested::marker': {
    content: '""',
    display: 'none',
  },
  '.rt-text-bold': { fontWeight: 600 },
  '.rt-text-italic': { fontStyle: 'italic' },
  '.rt-text-underline': { textDecoration: 'underline' },
  '.rt-link': {
    color: backrs.colors['Brand/600'],
    textDecoration: 'underline',
    cursor: 'pointer',
  },
});

const richTextEditableSx: SxProps<Theme> = (theme) => {
  const { backrs } = theme;

  return {
    ...baseContentSx(theme),
    '.rt-content': {
      minHeight: '300px',
      maxHeight: '350px',
      overflowY: 'auto',
      padding: backrs.spacing['spacing-3xl'],
      border: `1px solid ${backrs.colors['Gray/300']}`,
      borderRadius: backrs.radius['radius-md'],
      '&:focus-visible': {
        outline: 'none',
        border: `1px solid ${backrs.colors['Gray/300']}`,
      },
    },
  };
};

const richTextReadOnlySx: SxProps<Theme> = (theme) => ({
  position: 'relative',
  ...baseContentSx(theme),
  '.rt-content': {
    padding: theme.backrs.spacing['spacing-md'],
  },
});
